import React, { useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import SelectComponent from "../../../components/Tenant/SelectComponent/SelectComponent";
import InputComponent from "../../../components/Tenant/InputComponent/InputComponent";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import { AiOutlineEdit, AiOutlineDelete } from "react-icons/ai";
import { HiOutlinePlus } from "react-icons/hi";
import { Menu } from "@headlessui/react";
import { toast } from "react-hot-toast";
import {
  useCreateTemplateMappingMutation,
  useFetchAllTemplateMappingsMutation,
  useUpdateTemplateMappingByIdMutation,
  useDeleteTemplateMappingByIdMutation,
} from "../../../features/tenant/qrPrinter/printerTemplateMappingApi";
import { useFetchSubcategoriesMutation } from "../../../features/tenant/productHeirarchy/productHeirarchyApi";

const PrinterTempelateMapping = () => {
  // ========== STATE ==========
  const [showModal, setShowModal] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form fields
  const [templateName, setTemplateName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState({
    id: -1,
    name: "Select Category",
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  // BTW file
  const [btwFile, setBtwFile] = useState(null);
  const [btwFileName, setBtwFileName] = useState("");

  // Categories list
  const [categoriesList, setCategoriesList] = useState([]);

  // ========== API HOOKS ==========
  const [
    fetchAllMappings,
    { data: mappingsData, error: mappingsError },
  ] = useFetchAllTemplateMappingsMutation();

  const [
    createMapping,
    { data: createData, error: createError },
  ] = useCreateTemplateMappingMutation();

  const [
    updateMapping,
    { data: updateData, error: updateError },
  ] = useUpdateTemplateMappingByIdMutation();

  const [
    deleteMapping,
    { data: deleteData, error: deleteError },
  ] = useDeleteTemplateMappingByIdMutation();

  const [
    fetchSubcategories,
    { data: subcategoriesData, error: subcategoriesError },
  ] = useFetchSubcategoriesMutation();

  // ========== FETCH DATA ON MOUNT ==========
  useEffect(() => {
    fetchAllMappings({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
    });

    fetchSubcategories({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
    });
  }, []);

  // ========== PROCESS SUBCATEGORIES ==========
  useEffect(() => {
    if (subcategoriesData && subcategoriesData.body) {
      const cats = subcategoriesData.body.map((cat) => ({
        id: cat.id,
        name: cat.name,
      }));
      setCategoriesList(cats);
    }

    if (subcategoriesError) {
      console.log("subcategoriesError", subcategoriesError);
      toast.error("Failed to load categories", { id: "subcatError" });
    }
  }, [subcategoriesData, subcategoriesError]);

  // ========== HANDLE ERRORS ==========
  useEffect(() => {
    if (mappingsError) {
      console.log("mappingsError", mappingsError);
      toast.error("Something went wrong fetching mappings", {
        id: "mappingsError",
      });
    }
  }, [mappingsError]);

  // ========== CREATE RESPONSE ==========
  useEffect(() => {
    if (createData) {
      toast.success("Template Mapping Created Successfully", {
        id: "createSuccess",
      });
      resetForm();
      setShowModal(false);
      refreshList();
    }

    if (createError) {
      console.log("createError", createError);
      toast.error("Failed to create mapping", { id: "createError" });
    }
  }, [createData, createError]);

  // ========== UPDATE RESPONSE ==========
  useEffect(() => {
    if (updateData) {
      toast.success("Template Mapping Updated Successfully", {
        id: "updateSuccess",
      });
      resetForm();
      setShowModal(false);
      refreshList();
    }

    if (updateError) {
      console.log("updateError", updateError);
      toast.error("Failed to update mapping", { id: "updateError" });
    }
  }, [updateData, updateError]);

  // ========== DELETE RESPONSE ==========
  useEffect(() => {
    if (deleteData) {
      toast.success("Deleted Successfully");
      refreshList();
    }

    if (deleteError) {
      toast.error("Something went wrong", { id: "deleteError" });
    }
  }, [deleteData, deleteError]);

  // ========== HELPERS ==========
  const refreshList = () => {
    fetchAllMappings({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
    });
  };

  const resetForm = () => {
    setTemplateName("");
    setSelectedCategory({ id: -1, name: "Select Category" });
    setPreviewImage(null);
    setImagePreviewUrl(null);
    setBtwFile(null);
    setBtwFileName("");
    setIsUpdate(false);
    setEditingId(null);
  };

  const getCategoryName = (categoryId) => {
    const cat = categoriesList.find((c) => c.id === categoryId);
    return cat ? cat.name : categoryId;
  };

  // ========== IMAGE HANDLING ==========
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Image size should be less than 2MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
        setImagePreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // ========== BTW FILE HANDLING ==========
  const handleBtwFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("BTW file size should be less than 10MB");
        return;
      }
      const ext = file.name.split(".").pop()?.toLowerCase();
      if (ext !== "btw") {
        toast.error("Only .btw files are allowed");
        e.target.value = "";
        return;
      }
      setBtwFile(file);
      setBtwFileName(file.name);
    }
  };

  // ========== BUILD FORM DATA ==========
  const buildFormData = () => {
    const formData = new FormData();
    formData.append("template_name", templateName.trim());
    formData.append("category_id", selectedCategory.id);

    if (btwFile) {
      formData.append("btw_file", btwFile);
    }

    if (previewImage) {
      formData.append("preview_image", previewImage);
    }

    return formData;
  };

  // ========== CREATE / UPDATE HANDLERS ==========
  const handleSubmit = () => {
    if (!templateName.trim()) {
      toast.error("Template Name is required");
      return;
    }
    if (selectedCategory.id === -1) {
      toast.error("Please select a category");
      return;
    }
    if (!btwFile) {
      toast.error("BTW file is required");
      return;
    }

    createMapping({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      body: buildFormData(),
    });
  };

  const handleUpdate = () => {
    if (!templateName.trim()) {
      toast.error("Template Name is required");
      return;
    }
    if (selectedCategory.id === -1) {
      toast.error("Please select a category");
      return;
    }

    updateMapping({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      id: editingId,
      body: buildFormData(),
    });
  };

  // ========== EDIT HANDLER ==========
  const handleEdit = (row) => {
    setIsUpdate(true);
    setEditingId(row.id);
    setTemplateName(row.template_name);

    // Find matching category
    const matchedCategory = categoriesList.find(
      (c) => c.id === row.category_id
    );
    if (matchedCategory) {
      setSelectedCategory(matchedCategory);
    } else {
      setSelectedCategory({ id: row.category_id, name: `ID: ${row.category_id}` });
    }

    if (row.preview_image) {
      setPreviewImage(row.preview_image);
      setImagePreviewUrl(row.preview_image);
    } else {
      setPreviewImage(null);
      setImagePreviewUrl(null);
    }

    // Show existing btw file name (file itself not re-downloaded)
    setBtwFile(null);
    setBtwFileName(row.__btw_file_name__ || "");

    setShowModal(true);
  };

  // ========== DELETE HANDLER ==========
  const handleDelete = (data) => {
    toast(
      (t) => (
        <span>
          <h6>Are you sure you want to delete this?</h6>
          <div className="mt-3 d-flex">
            <button
              className="btn btn-outline-danger btn-sm ml-auto mr-1"
              onClick={() => {
                deleteMapping({
                  token: sessionStorage.getItem("access_token"),
                  tenant_id: JSON.parse(
                    sessionStorage.getItem("vendorData")
                  ).tenant_id,
                  id: data.id,
                });
                toast.dismiss(t.id);
              }}
            >
              Yes
            </button>
            <button
              className="btn btn-outline-primary btn-sm mr-auto ml-1"
              onClick={() => {
                toast.dismiss(t.id);
              }}
            >
              No
            </button>
          </div>
        </span>
      ),
      {
        id: "deleteTemplateMapping",
      }
    );
  };

  // ========== TABLE COLUMNS ==========
  const columns = [
    {
      name: "SNo",
      selector: (row, idx) => idx + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "Template Name",
      selector: (row) => row.template_name,
      sortable: true,
      width: "200px",
    },
    {
      name: "BTW File",
      selector: (row) => row.__btw_file_name__ || "-",
      sortable: true,
      width: "200px",
    },
    {
      name: "Category",
      selector: (row) => getCategoryName(row.category_id),
      sortable: true,
      width: "200px",
    },
    {
      name: "Preview",
      cell: (row) =>
        row.preview_image ? (
          <img
            src={row.preview_image}
            alt="preview"
            className="w-12 h-12 object-cover rounded border"
          />
        ) : (
          <span className="text-gray-400 text-xs">No Image</span>
        ),
      width: "100px",
    },
    {
      name: "Created At",
      selector: (row) =>
        row.created_date
          ? new Date(row.created_date).toLocaleDateString()
          : "-",
      sortable: true,
      width: "130px",
    },
    {
      name: "",
      selector: (row) => (
        <button
          className="flex justify-center items-center rounded bg-blue-500 px-3 pb-1.5 break-normal pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#28a745] transition duration-150 ease-in-out hover:bg-blue-600 hover:shadow-[0_8px_9px_-4px_rgba(40,167,69,0.3),0_4px_18px_0_rgba(40,167,69,0.2)] focus:bg-blue-600 focus:shadow-[0_8px_9px_-4px_rgba(40,167,69,0.3),0_4px_18px_0_rgba(40,167,69,0.2)] focus:outline-none focus:ring-0 active:bg-blue-700 active:shadow-[0_8px_9px_-4px_rgba(40,167,69,0.3),0_4px_18px_0_rgba(40,167,69,0.2)]"
          onClick={() => handleEdit(row)}
        >
          <AiOutlineEdit className="mr-0.5" />
          Edit
        </button>
      ),
      width: "110px",
    },
    {
      name: "",
      selector: (row) => (
        <div>
          <button
            onClick={() => handleDelete(row)}
            className="flex justify-center items-center rounded bg-danger px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#dc4c64] transition duration-150 ease-in-out hover:bg-danger-600 hover:shadow-[0_8px_9px_-4px_rgba(220,76,100,0.3),0_4px_18px_0_rgba(220,76,100,0.2)] focus:bg-danger-600 focus:shadow-[0_8px_9px_-4px_rgba(220,76,100,0.3),0_4px_18px_0_rgba(220,76,100,0.2)] focus:outline-none focus:ring-0 active:bg-danger-700 active:shadow-[0_8px_9px_-4px_rgba(220,76,100,0.3),0_4px_18px_0_rgba(220,76,100,0.2)]"
          >
            <AiOutlineDelete className="mr-1" />
            Delete
          </button>
        </div>
      ),
      width: "140px",
    },
  ];

  const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme")).theme
    .color_shades;

  return (
    <>
      <div className="flex w-full bg-gray-200 h-full justify-center">
        <div className="w-full ">
          <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
            Printer Template Mapping
            <LinksComponent />
          </div>

          <div className="p-3">
            <div className="p-3 w-full bg-white shadow rounded-lg">
              <DataTableComponent
                keyField={(row) => row.SNo}
                columns={columns}
                data={
                  mappingsData && mappingsData.body
                    ? mappingsData.body.data
                    : []
                }
                totalRows={
                  mappingsData && mappingsData.body
                    ? mappingsData.body.count
                    : 0
                }
              ></DataTableComponent>

              {/* Floating Add Button */}
              <div
                className="fixed bottom-0 right-16 w-16 h-16 mr-10 mb-2 cursor-pointer"
                id="box_btn"
              >
                <div className="text-right">
                  <Menu className="relative inline-block text-left">
                    <div>
                      <Menu.Button
                        onClick={() => {
                          resetForm();
                          setShowModal(true);
                        }}
                        className="inline-flex justify-cente w-12 h-12 rounded-full text-sm font-medium text-white hover:bg-opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 "
                        style={{
                          backgroundColor: `${primaryColor["700"]}`,
                        }}
                      >
                        <HiOutlinePlus className="w-full h-full p-3" />
                      </Menu.Button>
                    </div>
                  </Menu>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== ADD/EDIT MODAL ========== */}
      {showModal && (
        <ModalComponent
          size={"md"}
          data={null}
          show={showModal}
          setShow={setShowModal}
        >
          <div className="w-full p-4">
            <h2 className="text-lg font-semibold mb-4">
              {isUpdate ? "Update" : "Add"} Template Mapping
            </h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                isUpdate ? handleUpdate() : handleSubmit();
              }}
            >
              <div className="space-y-4">
                {/* Template Name */}
                <div>
                  <InputComponent
                    name={"Template Name"}
                    type={"text"}
                    value={templateName}
                    setValue={setTemplateName}
                    required={true}
                  />
                </div>

                {/* Category Dropdown */}
                <div className="dropds">
                  <SelectComponent
                    title={"Category"}
                    selected={selectedCategory}
                    setSelected={setSelectedCategory}
                    dataArr={categoriesList}
                  />
                </div>

                {/* BTW File Upload */}
                <div>
                  <label className="block text-sm font-medium capitalize leading-6 text-gray-700 mb-2">
                    BTW File {isUpdate ? "(Optional - upload to replace)" : "(Required)"}
                  </label>
                  <input
                    type="file"
                    accept=".btw"
                    onChange={handleBtwFileChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                  />
                  {btwFileName && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        📄 {btwFileName}
                      </span>
                      {btwFile && (
                        <button
                          type="button"
                          onClick={() => {
                            setBtwFile(null);
                            setBtwFileName("");
                          }}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Preview Image Upload */}
                <div>
                  <label className="block text-sm font-medium capitalize leading-6 text-gray-700 mb-2">
                    Preview Image (Optional)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {imagePreviewUrl && (
                    <div className="mt-3">
                      <img
                        src={imagePreviewUrl}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPreviewImage(null);
                          setImagePreviewUrl(null);
                        }}
                        className="mt-1 text-xs text-red-500 hover:text-red-700"
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end mt-6 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowModal(false);
                  }}
                  className="bg-gray-500 hover:bg-gray-400 rounded-sm text-white font-bold py-2 px-4"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                >
                  {isUpdate ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </ModalComponent>
      )}
    </>
  );
};

export default PrinterTempelateMapping;
